import { inject, Injectable } from '@angular/core';
import { AuthResponse, AuthUser, InviteUserMetaData, LoginDto, RegisterDto } from '../models/auth.types';
import { catchError, from, map, Observable, of, switchMap, throwError } from 'rxjs';
import { Supabase } from './supabase';
import { TranslateService } from '@ngx-translate/core';
import { Database } from './database';

@Injectable({
  providedIn: 'root'
})
export class Auth {
  private supabase = inject(Supabase);
  private translate = inject(TranslateService);
  private database = inject(Database);

  login(loginDto: LoginDto): Observable<AuthResponse> {
    const supabase = this.supabase.getClient();
    
    return from(
      supabase.auth.signInWithPassword({
        email: loginDto.email,
        password: loginDto.password
      })
    ).pipe(
      switchMap(({ data, error }) => {
        if (error) {
          return of({
            user: null,
            session: null,
            error: this.translateSupabaseError(error.message)
          });
        }

        if (!data.user) {
          return of({
            user: null,
            session: data.session,
            error: null
          });
        }

        // Fetch user profile data using generic getById function
        return this.database.getUserById(data.user.id).pipe(
          map((userData) => {
            const authUser: AuthUser = {
              id: data.user.id,
              email: data.user.email!,
              emailConfirmed: data.user.email_confirmed_at !== null,
              createdAt: data.user.created_at,
              firstName: userData?.first_name || undefined,
              lastName: userData?.last_name || undefined,
              companyId: userData?.company_id || undefined,
            };

            return {
              user: authUser,
              session: data.session,
              error: null
            };
          }),
          catchError(() => {
            // If profile fetch fails, return basic user data
            const authUser: AuthUser = {
              id: data.user.id,
              email: data.user.email!,
              emailConfirmed: data.user.email_confirmed_at !== null,
              createdAt: data.user.created_at
            };

            return of({
              user: authUser,
              session: data.session,
              error: null
            });
          })
        );
      }),
      catchError(() => {
        return throwError(() => ({
          user: null,
          session: null,
          error: this.translate.instant('auth.errors.loginFailed')
        }));
      })
    );
  }

  public register(registerDto: RegisterDto): Observable<AuthResponse> {
    const supabase = this.supabase.getClient();
    
    return from (supabase.auth.signUp({
      email: registerDto.email,
      password: registerDto.password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/onboarding`
      }
    })).pipe(
      map(({ data, error }) => {
        if (error) {
          return {
            user: null,
            session: null,
            error: this.translateSupabaseError(error.message)
          };
        }

        const authUser: AuthUser | null = data.user ? {
          id: data.user.id,
          email: data.user.email!,
          emailConfirmed: data.user.email_confirmed_at !== null,
          createdAt: data.user.created_at
        } : null;

        return {
          user: authUser,
          session: data.session,
          error: null
        };
      }),
      catchError(() => {
        return throwError(() => ({
          user: null,
          session: null,
          error: this.translate.instant('auth.errors.registrationFailed')
        }));
      })
    );
  }

  getCurrentUserId(): Promise<string | null> {
    const supabase = this.supabase.getClient();
    return supabase.auth.getSession().then(({ data, error }) => {
      if (error || !data.session?.user) {
        return null;
      }
      return data.session.user.id;
    });
  }

  async signOut(): Promise<void> {
    const supabase = this.supabase.getClient();
    await supabase.auth.signOut();
  }

  updateUser(userId: string, updates: { firstName?: string; lastName?: string; language?: string; jobTitle?: string; password?: string }): Observable<{ success: boolean; error: string | null }> {
    const updateData: Record<string, string> = {};
    
    if (updates.firstName !== undefined) {
      updateData['first_name'] = updates.firstName;
    }
    if (updates.lastName !== undefined) {
      updateData['last_name'] = updates.lastName;
    }
    if (updates.language !== undefined) {
      updateData['language'] = updates.language;
    }
    if (updates.jobTitle !== undefined) {
      updateData['job_title'] = updates.jobTitle;
    }

    // Handle password update separately via Supabase Auth
    if (updates.password !== undefined) {
      const supabase = this.supabase.getClient();
      return from(supabase.auth.updateUser({ password: updates.password })).pipe(
        switchMap(({ error: authError }) => {
          if (authError) {
            return of({
              success: false,
              error: this.translateSupabaseError(authError.message)
            });
          }
          
          // If there are other updates, apply them to the database
          if (Object.keys(updateData).length > 0) {
            return this.database.updateUser(userId, updateData).pipe(
              map(() => ({
                success: true,
                error: null
              })),
              catchError(() => {
                return of({
                  success: false,
                  error: this.translate.instant('auth.errors.updateFailed')
                });
              })
            );
          }
          
          return of({
            success: true,
            error: null
          });
        }),
        catchError(() => {
          return of({
            success: false,
            error: this.translate.instant('auth.errors.updateFailed')
          });
        })
      );
    }

    return this.database.updateUser(userId, updateData).pipe(
      map(() => ({
        success: true,
        error: null
      })),
      catchError(() => {
        return of({
          success: false,
          error: this.translate.instant('auth.errors.updateFailed')
        });
      })
    );
  }

  resetPassword(email: string): Observable<{ success: boolean; error: string | null }> {
    const supabase = this.supabase.getClient();
    
    return from(
      supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/recover`
      })
    ).pipe(
      map(({ error }) => {
        if (error) {
          return {
            success: false,
            error: this.translateSupabaseError(error.message)
          };
        }
        return {
          success: true,
          error: null
        };
      }),
      catchError(() => {
        return of({
          success: false,
          error: this.translate.instant('auth.errors.resetPasswordFailed')
        });
      })
    );
  }

  inviteUserByEmail(email: string, metadata: InviteUserMetaData): Observable<{ success: boolean; error: string | null; details?: { plan: string; maxUsers: number; currentUsers: number } }> {
    return this.supabase.invokeFunction<{ success: boolean; error?: string; details?: { plan: string; maxUsers: number; currentUsers: number } }>('invite-user', {
      email,
      metadata
    }).pipe(
      map((response) => {
        if (response.error) {
          return {
            success: false,
            error: this.translateSupabaseError(response.error),
            details: response.details
          };
        }
        return {
          success: true,
          error: null
        };
      }),
      catchError((error) => {
        // Try to extract details from error response
        let details: { plan: string; maxUsers: number; currentUsers: number } | undefined;
        const errorMessage = this.translate.instant('auth.errors.inviteFailed');
        
        // error.context is a Response object, we need to parse its body
        if (error.context instanceof Response) {
          // Parse the response body asynchronously
          return from(error.context.json() as Promise<{ details?: { plan: string; maxUsers: number; currentUsers: number } }>).pipe(
            map((body) => {
              if (body.details) {
                details = body.details;
              }
              return {
                success: false,
                error: errorMessage,
                details
              };
            }),
            catchError(() => {
              // If JSON parsing fails, return without details
              return of({
                success: false,
                error: errorMessage,
                details: undefined
              });
            })
          );
        }
        
        return of({
          success: false,
          error: errorMessage,
          details
        });
      })
    );
  }

  private translateSupabaseError(error: string): string {
    const errorMap: Record<string, string> = {
      'User already registered': 'auth.errors.userAlreadyRegistered',
      'Invalid login credentials': 'auth.errors.invalidLoginCredentials',
      'Email not confirmed': 'auth.errors.emailNotConfirmed',
      'Password should be at least 6 characters': 'auth.errors.passwordTooShort',
      'Invalid email': 'auth.errors.invalidEmail',
      'Signup requires a valid password': 'auth.errors.signupRequiresPassword'
    };

    const translationKey = errorMap[error] || 'auth.errors.genericError';
    return this.translate.instant(translationKey);
  }
}
